import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Divider,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useProductionById } from 'hooks';
import Loading from 'components/Loading';
import {
  checkDateUpperTimeInterval,
  createUTCDateFromDateString,
  formatDateForDisplay,
  formatHourForDisplay,
} from 'utils/date';
import { formatTonsFromKg } from 'utils/weight';
import { colors } from 'theme';
import { Routes } from 'routes';
import { AppContext } from 'context';
import { useLatestClosureDate } from 'utils/closureHooks';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteProduction } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { goBack } from 'utils/history';
import { UserRole } from '../../types';

const useStyles = makeStyles({
  marginTopDivider: {
    marginTop: '3em',
  },
  label: {
    fontWeight: 600,
    marginRight: 16,

    '&:after': {
      content: '" :"',
    },
  },
  tablePaper: {
    maxWidth: '65%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  noBorder: {
    borderBottom: 'none',
  },
});

const ProductionShow: React.FC = () => {
  const classes = useStyles();

  const history = useHistory();

  const { productionId } = useParams<{ productionId: string }>();

  const { t } = useTranslation();
  const { value: production, loading } = useProductionById(productionId);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const productionDatetime = production ? createUTCDateFromDateString(production.productionDate) : new Date();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const canEdit = production ? !readOnly && !checkDateUpperTimeInterval(productionDatetime, closureMonth) : false;
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  return (
    <>
      <NoPaperPageContent
        title={t('productions.show.title')}
        backAction={() => goBack(Routes.Productions, history)}
        updateButtonLink={
          canEdit
            ? {
                pathname: generatePath(Routes.ProductionEdit, { productionId }),
                state: { referer: generatePath(Routes.ProductionShow, { productionId }) },
              }
            : undefined
        }
        deleteButtonAction={canEdit ? () => setConfirmDeleteModalOpen(true) : undefined}
      >
        {loading ? (
          <Loading />
        ) : (
          production && (
            <>
              <DeleteConfirmationDialog
                title={t('productionForm.deletion.confirm')}
                isOpen={confirmDeleteModalOpen}
                setOpen={open => setConfirmDeleteModalOpen(open)}
                confirmDeletion={async () => {
                  setConfirmDeleteModalOpen(false);

                  await deleteProduction(productionId);
                  notify({
                    message: t('productionForm.deletion.success'),
                    severity: 'success',
                  });
                  goBack(Routes.Productions, history);
                }}
              />
              <Divider className={classes.marginTopDivider} />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{t('productionForm.productionDate')}</span>
                    {formatDateForDisplay(production.productionDate)}
                  </Typography>
                </Box>
                <Typography>
                  <span className={classes.label}>{t('productionForm.productionTime')}</span>
                  {formatHourForDisplay(production.productionDate)}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Typography>
                  <span className={classes.label}>{t('productionForm.create.material')}</span>
                  {production.material.shortName}
                </Typography>
              </Box>
              <Paper square className={classes.tablePaper}>
                <Box display="flex" marginTop="4em" paddingLeft="21px" paddingRight="21px">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="right">{t('productionForm.create.conditioningQuantity')}</TableCell>
                        <TableCell align="right">{t('productionForm.create.ballAverageWeight')}</TableCell>
                        <TableCell align="right">{t('productionForm.create.totalWeight')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="right" className={classes.noBorder}>
                          {production.conditioningQuantity ?? '-'}
                        </TableCell>
                        <TableCell align="right" className={classes.noBorder}>
                          {production.conditioningQuantity
                            ? formatTonsFromKg(production.weight / production.conditioningQuantity)
                            : '-'}
                        </TableCell>
                        <TableCell align="right" className={classes.noBorder}>
                          {formatTonsFromKg(production.weight)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </>
          )
        )}
      </NoPaperPageContent>
    </>
  );
};
export default ProductionShow;
